import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import React from 'react';

import { type PhoebeFooterStatus } from '@apps/web/components/phoebe_chat/event_transcript';
import { type LiveRunStatus } from '@apps/web/components/phoebe_chat/streaming_types';
import { PhoebeThinkingTipLine } from '@apps/web/components/phoebe_chat_view/run_status/shimmer_status';
import { getToolIcon } from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_renderers';
import { Button } from '@apps/web/components/ui/button';
import { cn } from '@apps/web/lib/utils';

export function LiveStatusRow({
    status,
    isExpandable = false,
    isExpanded = false,
    onToggle,
    showSpinner = true,
    presentation = 'default',
    tip = null,
}: {
    status: LiveRunStatus;
    isExpandable?: boolean;
    isExpanded?: boolean;
    onToggle?: () => void;
    showSpinner?: boolean;
    presentation?: 'default' | 'workbench';
    tip?: string | null;
}) {
    const usesWorkbenchPresentation = presentation === 'workbench';
    const toolIcon =
        status.phase === 'tool' && status.toolName
            ? (getToolIcon(status.toolName) ?? null)
            : null;
    const ToolIcon = toolIcon;
    const content = (
        <React.Fragment>
            {ToolIcon ? (
                <ToolIcon className="h-4 w-4" />
            ) : showSpinner ? (
                <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            <span>{status.label}</span>
            {status.detail ? (
                <span className="text-foreground/70 max-w-[20rem] truncate font-medium">
                    {status.detail}
                </span>
            ) : null}
            {isExpandable ? (
                isExpanded ? (
                    <ChevronDown className="text-muted-foreground/70 dark:text-muted-foreground h-3 w-3" />
                ) : (
                    <ChevronRight className="text-muted-foreground/70 dark:text-muted-foreground h-3 w-3" />
                )
            ) : null}
        </React.Fragment>
    );

    return (
        <div className="flex justify-start">
            {isExpandable && onToggle ? (
                <div>
                    <button
                        type="button"
                        onClick={onToggle}
                        className={cn(
                            'flex cursor-pointer items-center gap-2 transition-colors',
                            usesWorkbenchPresentation
                                ? 'rounded-full border border-[#DBE3E3] bg-[#FFFFFF]/90 px-2.5 py-1 text-[11px] font-medium text-[#42595E] shadow-[0_1px_0_rgba(0,36,41,0.04)] hover:border-[#DBE3E3] hover:text-[#002429]'
                                : 'text-muted-foreground/70 dark:text-muted-foreground hover:text-foreground py-2 text-xs',
                        )}
                    >
                        {content}
                    </button>
                    <PhoebeThinkingTipLine tip={tip} />
                </div>
            ) : (
                <div>
                    <div
                        className={cn(
                            'flex items-center gap-2',
                            usesWorkbenchPresentation
                                ? 'rounded-full border border-[#DBE3E3] bg-[#FFFFFF]/90 px-2.5 py-1 text-[11px] font-medium text-[#42595E] shadow-[0_1px_0_rgba(0,36,41,0.04)]'
                                : 'text-muted-foreground/70 dark:text-muted-foreground py-2 text-xs',
                        )}
                    >
                        {content}
                    </div>
                    <PhoebeThinkingTipLine tip={tip} />
                </div>
            )}
        </div>
    );
}

export function PhoebeFooterStatusRow({
    status,
    onRetryAfterRunError,
    onStopMonitoring,
    isRetryDisabled = false,
    isStopMonitoringDisabled = false,
}: {
    readonly status: PhoebeFooterStatus;
    readonly onRetryAfterRunError?: () => void;
    readonly onStopMonitoring?: () => void;
    readonly isRetryDisabled?: boolean;
    readonly isStopMonitoringDisabled?: boolean;
}) {
    return (
        <div className="flex justify-start">
            <div className="text-muted-foreground flex flex-wrap items-center gap-2 py-2 text-sm">
                {status.showSpinner && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                )}
                <span>{status.label}</span>
                {status.detail ? (
                    <span className="text-foreground max-w-md break-words">
                        {status.detail}
                    </span>
                ) : null}
                {status.canRetryRunError && onRetryAfterRunError ? (
                    <button
                        type="button"
                        disabled={isRetryDisabled}
                        onClick={onRetryAfterRunError}
                        className="text-destructive cursor-pointer rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-900 dark:bg-red-950/50 dark:hover:bg-red-900/40"
                    >
                        Click here to retry
                    </button>
                ) : null}
                {status.canStopMonitoring && onStopMonitoring ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isStopMonitoringDisabled}
                        onClick={onStopMonitoring}
                        className="h-7 px-2 text-xs"
                    >
                        Stop monitoring
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
